import React, { useState, useRef } from "react";

import {
  Box,
  Button,
  Divider,
  Flex,
  Select,
  SimpleGrid,
  TextInput,
  createStyles,
  rem,
  Loader,
} from "@mantine/core";

import { removeFacility } from "../../services";
import { RiDeleteBin7Line } from "react-icons/ri";
import swal from "sweetalert";
import ActionButtons from "./ActionButtons";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg";
import { stateZipCodes } from "../../utils/zipCodes";
import {
  searchAddress,
  getAddressByPlaceId,
} from "../../services/dashboard";

const useStyles = createStyles((theme) => ({
  FacilityDetailsTab: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  facilityForm: {
    minWidth: "700px",
    [theme.fn.smallerThan("sm")]: {
      minWidth: "unset",
      width: "100%",
    },
  },
  autocompleteWrapper: {
    position: "relative",
  },
  autocompleteDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 100,
    background: "#fff",
    border: "1px solid #dee2e6",
    borderRadius: 8,
    maxHeight: 200,
    overflowY: "auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  autocompleteItem: {
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: 14,
    "&:hover": {
      background: "#f8f9fa",
    },
  },
}));

export const FacilityDetailsTab = ({
  mode,
  form,
  activeTab,
  handleSave,
  handleCancel,
  loading,
  selectedStateAbbr,
  ...data
}) => {
  const DropDownArrow = (
    <DropDownArrowSvg
      color={"#FBAA0D"}
      style={{ width: rem(1), height: rem(1) }}
    />
  );
  const { classes } = useStyles();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const debounceRef = useRef(null);

  const handleAddressSearch = (value) => {
    form.setFieldValue("address", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await searchAddress(value);
        setAddressSuggestions(results || []);
        setShowSuggestions(true);
      } catch (e) {
        setAddressSuggestions([]);
      }
      setAddressLoading(false);
    }, 400);
  };

  const handleSelectAddress = async (suggestion) => {
    setShowSuggestions(false);
    setAddressLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const components =
        placeDetails?.result?.address_components || [];

      let streetNumber = "";
      let route = "";
      let city = "";
      let state = "";
      let zipcode = "";

      for (const comp of components) {
        if (comp.types.includes("street_number")) streetNumber = comp.long_name;
        if (comp.types.includes("route")) route = comp.long_name;
        if (comp.types.includes("locality"))
          city = comp.long_name;
        if (
          comp.types.includes("administrative_area_level_1")
        )
          state = comp.long_name;
        if (comp.types.includes("postal_code")) zipcode = comp.long_name;
      }

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      form.setFieldValue("address", streetAddress || suggestion.formatted_address);
      if (city) form.setFieldValue("location", city);
      if (state) form.setFieldValue("state", state);
      if (zipcode) form.setFieldValue("zip", zipcode);
    } catch (e) {
      form.setFieldValue("address", suggestion.formatted_address);
    }
    setAddressLoading(false);
  };

  const handleDeleteFacility = async (event) => {
    event.stopPropagation();
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone. Facility are further associated to Client, Deleting facility can cause problems.",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) removeFacility(data?.data);
  };

  return (
    <Box>
      {mode !== "add" && (
        <>
          <Flex justify={"flex-end"} mt={10}>
            <Button
              c={"red"}
              color="gray"
              variant="transparent"
              className={classes.delete}
              onClick={handleDeleteFacility}
            >
              <RiDeleteBin7Line size={18} />
              <span style={{ marginLeft: "5px" }}>Delete</span>
            </Button>
          </Flex>
          <Divider my={10} />
        </>
      )}
      <Box className={classes.FacilityDetailsTab}>
        <form onSubmit={form.onSubmit(handleSave)}>
          <Box className={classes.facilityForm}>
            <SimpleGrid
              cols={2}
              breakpoints={[{ maxWidth: "sm", cols: 1 }]}
              className={classes.mwd}
              sx={{
                paddingTop: 20,
              }}
            >
              {/* facility name */}
              <TextInput
                id="facility-name"
                label="Facility Name"
                variant="default"
                size="md"
                radius={"md"}
                placeholder="Ex: Valerie Liberty"
                {...form.getInputProps("name")}
                disabled={mode == "view" ? true : false}
              />
              <TextInput
                id="Phone"
                label="Phone Number"
                variant="default"
                size="md"
                radius={"md"}
                placeholder="Ex: 1234567890"
                {...form.getInputProps("phone")}
                disabled={mode == "view" ? true : false}
              />
            </SimpleGrid>
            <TextInput
              id="address"
              label="Email"
              size="md"
              radius={"md"}
              placeholder="Email Address"
              sx={{ marginTop: 20 }}
              {...form.getInputProps("email")}
              disabled={mode == "view" ? true : false}
            />
            {/* address with Google autocomplete */}
            <Box className={classes.autocompleteWrapper} sx={{ marginTop: 20 }}>
              <TextInput
                id="address"
                label="Address"
                size="md"
                radius={"md"}
                placeholder="Start typing an address..."
                value={form.values.address}
                onChange={(e) => handleAddressSearch(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() =>
                  addressSuggestions.length > 0 && setShowSuggestions(true)
                }
                disabled={mode == "view" ? true : false}
                error={form.errors.address}
                rightSection={addressLoading ? <Loader size="xs" /> : null}
              />
              {showSuggestions && addressSuggestions.length > 0 && (
                <Box className={classes.autocompleteDropdown}>
                  {addressSuggestions.map((s, i) => (
                    <Box
                      key={s.place_id || i}
                      className={classes.autocompleteItem}
                      onMouseDown={() => handleSelectAddress(s)}
                    >
                      {s.formatted_address}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            {/* city */}
            <TextInput
              id="address-two"
              label="City"
              size="md"
              radius={"md"}
              sx={{ marginTop: 20 }}
              placeholder="Ex: City Name"
              {...form.getInputProps("location")}
              disabled={mode == "view" ? true : false}
            />
            {/* zipcode */}
            <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]} sx={{ marginTop: 20 }}>
              <TextInput
                id="zipcode"
                label="Zipcode"
                size="md"
                radius={"md"}
                placeholder="Ex: 00001"
                type="number"
                {...form.getInputProps("zip")}
                disabled={mode == "view" ? true : false}
              />
              {/* state */}
              <Select
                id="state"
                searchable
                label="State"
                data={Array.from(
                  new Set(stateZipCodes?.map((obj) => obj.ste_name))
                )
                  .sort()
                  .map((ste_name) => ({
                    value: ste_name,
                    label: ste_name,
                  }))}
                size="md"
                radius={"md"}
                value={selectedStateAbbr}
                placeholder="Choose One"
                {...form.getInputProps("state")}
                disabled={mode == "view" ? true : false}
                rightSection={DropDownArrow}
              />
            </SimpleGrid>
            <SimpleGrid mt={10}>
              <TextInput
                label="License Number"
                radius="md"
                size="md"
                placeholder="Add License Number"
                {...form.getInputProps("providerLicense")}
                disabled={mode == "view" ? true : false}
              />
            </SimpleGrid>
          </Box>
          {(mode === "add" || mode === "edit") && (
            <Box mt={40}>
              <Divider my={10} />
              <ActionButtons
                onCancel={handleCancel}
                type="submit"
                loading={loading}
                isFacility={true}
                activeTab={activeTab}
                // onSave={handleSave}
              />
            </Box>
          )}
        </form>
      </Box>
    </Box>
  );
};
